import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { IsOptional, IsString, IsInt } from 'class-validator';
import { Type } from 'class-transformer';

/**
 * DTO para filtros del Dashboard de Control DUS.
 * Todos los filtros son opcionales y se aplican a todos los widgets.
 */
export class ControlDusDashboardFiltersDto {
  @ApiPropertyOptional({
    description: 'ID del cliente (opcional, auto-aplicado si es rol client)',
    example: 66,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  clienteId?: number;

  @ApiPropertyOptional({
    description: 'Nombre de la nave (opcional)',
    example: 'MSC SANTA MARIA',
  })
  @IsOptional()
  @IsString()
  nave?: string;

  @ApiPropertyOptional({
    description: 'Nombre del ejecutivo (opcional)',
    example: 'Jorge Alvarado',
  })
  @IsOptional()
  @IsString()
  ejecutivo?: string;

  @ApiPropertyOptional({
    description: 'Tipo de embarque (opcional)',
    example: 'Contenedor',
    enum: ['Contenedor', 'Pallet'],
  })
  @IsOptional()
  @IsString()
  tipoEmbarque?: string;
}

/**
 * DTO para el widget de Estado y Etapa.
 * Muestra distribución de DUS por etapa y estado.
 */
export class DashboardEstadoEtapaDto {
  @ApiProperty({
    description: 'Métricas de etapa Documentación',
    example: {
      pendiente: 4,
      enProceso: 0,
      finalizado: 0,
      total: 4,
    },
  })
  documentacion!: {
    pendiente: number;
    enProceso: number;
    finalizado: number;
    total: number;
  };

  @ApiProperty({
    description: 'Métricas de etapa Legalización',
    example: {
      pendiente: 8,
      enProceso: 0,
      finalizado: 3513,
      total: 3521,
    },
  })
  legalizacion!: {
    pendiente: number;
    enProceso: number;
    finalizado: number;
    total: number;
  };

  @ApiProperty({
    description: 'DUS sin etapa definida',
    example: 263,
  })
  sinEtapa!: number;
}

/**
 * DTO para el widget de Rango de Vencimiento.
 * Muestra distribución de DUS por rango de vencimiento.
 */
export class DashboardRangoVencimientoDto {
  @ApiProperty({
    description: 'DUS vencidos',
    example: { total: 3623, porcentaje: 98.02 },
  })
  vencido!: { total: number; porcentaje: number };

  @ApiProperty({
    description: 'DUS por vencer (próximos 7 días)',
    example: { total: 73, porcentaje: 1.98 },
  })
  porVencer!: { total: number; porcentaje: number };

  @ApiProperty({
    description: 'DUS vigentes',
    example: { total: 0, porcentaje: 0 },
  })
  vigente!: { total: number; porcentaje: number };
}

/**
 * DTO para item individual de tipo de atraso.
 */
export class DashboardTipoAtrasoItemDto {
  @ApiProperty({
    description: 'Tipo de atraso',
    example: 'SIN INFORMACION',
  })
  tipo!: string;

  @ApiProperty({
    description: 'Total de DUS con este tipo de atraso',
    example: 2665,
  })
  total!: number;
}

/**
 * DTO para el widget de Tipo de Atraso.
 * Muestra distribución de DUS por tipo de atraso.
 */
export class DashboardTipoAtrasoDto {
  @ApiProperty({
    description: 'Lista de tipos de atraso con sus totales',
    type: [DashboardTipoAtrasoItemDto],
  })
  datos!: DashboardTipoAtrasoItemDto[];

  @ApiProperty({
    description: 'Total de DUS con tipo de atraso definido',
    example: 3788,
  })
  totalDus!: number;
}

/**
 * DTO para el widget de Prórroga.
 * Muestra distribución de DUS con/sin prórroga.
 */
export class DashboardProrrogaDto {
  @ApiProperty({
    description: 'DUS con prórroga',
    example: { total: 2411, porcentaje: 63.77 },
  })
  conProrroga!: { total: number; porcentaje: number };

  @ApiProperty({
    description: 'DUS sin prórroga',
    example: { total: 1370, porcentaje: 36.23 },
  })
  sinProrroga!: { total: number; porcentaje: number };
}

/**
 * DTO para el widget de Estado Aduana.
 * Muestra total de estados aduaneros distintos.
 */
export class DashboardEstadoAduanaDto {
  @ApiProperty({
    description: 'Total de estados aduaneros distintos',
    example: 6,
  })
  totalEstados!: number;
}

/**
 * DTO de respuesta completa con todas las métricas del dashboard.
 */
export class ControlDusDashboardMetricsDto {
  @ApiProperty({
    description: 'Widget 1: DUS por Estado y Etapa',
    type: DashboardEstadoEtapaDto,
  })
  estadoEtapa!: DashboardEstadoEtapaDto;

  @ApiProperty({
    description: 'Widget 2: DUS por Rango de Vencimiento',
    type: DashboardRangoVencimientoDto,
  })
  rangoVencimiento!: DashboardRangoVencimientoDto;

  @ApiProperty({
    description: 'Widget 3: DUS por Tipo de Atraso',
    type: DashboardTipoAtrasoDto,
  })
  tipoAtraso!: DashboardTipoAtrasoDto;

  @ApiProperty({
    description: 'Widget 4: Total DUS',
    example: 3788,
  })
  totalDus!: number;

  @ApiProperty({
    description: 'Widget 5: DUS por Prórroga',
    type: DashboardProrrogaDto,
  })
  prorroga!: DashboardProrrogaDto;

  @ApiProperty({
    description: 'Widget 6: Estado Aduana',
    type: DashboardEstadoAduanaDto,
  })
  estadoAduana!: DashboardEstadoAduanaDto;

  @ApiProperty({
    description: 'Widget 7: Total Clientes',
    example: 28,
  })
  totalClientes!: number;

  @ApiProperty({
    description: 'Timestamp de generación de las métricas',
    example: '2025-10-26T15:30:00.000Z',
  })
  timestamp!: string;

  @ApiProperty({
    description: 'Filtros aplicados en esta consulta',
    type: ControlDusDashboardFiltersDto,
  })
  filtrosAplicados!: ControlDusDashboardFiltersDto;
}
